// FieldIQ — Rich text for calendar descriptions.
//
// Google Calendar's description box is a rich-text editor, so its ICS export
// puts HTML straight into DESCRIPTION — there is no X-ALT-DESC in Google's
// feed to fall back to. The sync stores that verbatim in events.agenda, so the
// app is the only place it can be rendered. The content is third-party —
// anyone who can add an event to the facility's calendar writes it — so this
// uses an explicit allowlist rather than ChatTab's bare sanitize of our own
// enrichment HTML.

import DOMPurify from "dompurify";

// The subset Google's editor actually emits. Anything else is dropped; its
// text content survives (DOMPurify KEEP_CONTENT defaults on), so a stray
// wrapper never swallows the description.
const ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "a",
  "br",
  "p",
  "ul",
  "ol",
  "li",
];
const ALLOWED_ATTR = ["href", "target", "rel"];

const HAS_TAGS = /<[a-z][^>]*>/i;
const HAS_ENTITIES = /&(?:amp|lt|gt|quot|#0?39|apos|nbsp);/i;

// Tailwind is CDN-loaded (public/index.html), so Preflight is active: it
// strips bullets off lists, margins off paragraphs, and colour off links.
// Every tag we allow has to have its styling put back explicitly or formatted
// descriptions render as an unstyled run-on.
const PROSE =
  "[&_p]:mb-2 [&_p:last-child]:mb-0 " +
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ul:last-child]:mb-0 " +
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_ol:last-child]:mb-0 " +
  "[&_li]:mb-0.5 " +
  "[&_a]:text-cyan-400 [&_a]:underline [&_a]:break-words " +
  "[&_b]:font-semibold [&_strong]:font-semibold";

const sanitizeDescription = (html) => {
  const frag = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });
  // Send every surviving link out to the browser. FieldIQ is an installed
  // PWA — an in-place navigation would replace the app shell and lose the
  // user's place. Done by walking the fragment rather than with a DOMPurify
  // hook: hooks register on the shared singleton and would silently change
  // ChatTab's sanitize calls too.
  frag.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  const holder = document.createElement("div");
  holder.appendChild(frag);
  return holder.innerHTML;
};

// A rich-text description with no actual formatting still arrives
// entity-escaped ("Tom &amp; Jerry"). The decoded string is rendered as a
// React text child, so it is re-escaped on the way out.
const decodeEntities = (text) => {
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
};

export const RichText = ({ text, className = "" }) => {
  if (!text) return null;

  if (HAS_TAGS.test(text)) {
    return (
      <div
        className={`${className} ${PROSE}`}
        // Allowlist-sanitized directly above.
        dangerouslySetInnerHTML={{ __html: sanitizeDescription(text) }}
      />
    );
  }

  // Plain text: icalendar has already turned the iCal \n escapes into real
  // newlines, so they need preserving — HTML rendering would collapse them.
  return (
    <p className={`${className} whitespace-pre-wrap`}>
      {HAS_ENTITIES.test(text) ? decodeEntities(text) : text}
    </p>
  );
};
